/**
 * File Validation Helper
 * Implements secure file upload validation to prevent malicious file uploads (CWE-434)
 * 
 * Security measures:
 * 1. Whitelist-based file extension validation
 * 2. File content validation using magic numbers (file signatures)
 * 3. File size validation
 * 4. Filename sanitization
 * 5. Double extension detection
 */

// Maximum file size: 20MB
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

// Allowed file extensions (whitelist approach)
export const ALLOWED_EXTENSIONS = {
    'jpg': true,
    'jpeg': true,
    'pdf': true
};

// File magic numbers (file signatures) for content validation
// These are the first few bytes that identify the file type
const FILE_SIGNATURES = {
    // JPEG files start with FF D8 FF
    'jpeg': [
        [0xFF, 0xD8, 0xFF, 0xDB], // JPEG raw
        [0xFF, 0xD8, 0xFF, 0xE0], // JPEG/JFIF
        [0xFF, 0xD8, 0xFF, 0xE1], // JPEG/Exif
        [0xFF, 0xD8, 0xFF, 0xE2], // JPEG/Canon
        [0xFF, 0xD8, 0xFF, 0xE3], // JPEG/Samsung
        [0xFF, 0xD8, 0xFF, 0xE8]  // JPEG/SPIFF
    ],
    // PDF files start with %PDF (25 50 44 46)
    'pdf': [
        [0x25, 0x50, 0x44, 0x46]
    ]
};

/**
 * Read the first few bytes of a file to check its magic number
 * @param {File} file - The file to check
 * @param {number} bytesToRead - Number of bytes to read
 * @returns {Promise<Uint8Array>} - Array of bytes
 */
const readFileHeader = (file, bytesToRead = 4) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const arr = new Uint8Array(e.target.result);
            resolve(arr);
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        // Read only the first few bytes
        const blob = file.slice(0, bytesToRead);
        reader.readAsArrayBuffer(blob);
    });
};

/**
 * Check if byte array matches any of the given signatures
 * @param {Uint8Array} bytes - Bytes to check
 * @param {Array} signatures - Array of signature arrays
 * @returns {boolean} - True if matches any signature
 */
const matchesSignature = (bytes, signatures) => {
    return signatures.some(signature => {
        // Check if all bytes in signature match
        return signature.every((byte, index) => bytes[index] === byte);
    });
};

/**
 * Validate file extension (whitelist approach)
 * @param {string} filename - Name of the file
 * @returns {Object} - { isValid: boolean, extension: string, error: string }
 */
export const validateFileExtension = (filename) => {
    if (!filename) {
        return { isValid: false, extension: '', error: 'Filename is required' };
    }

    // Convert to lowercase for case-insensitive comparison
    const lowerFilename = filename.toLowerCase();

    // Check for double extensions (e.g., file.pdf.exe)
    const parts = lowerFilename.split('.');
    if (parts.length > 2) {
        return {
            isValid: false,
            extension: '',
            error: 'Files with multiple extensions are not allowed for security reasons'
        };
    }

    // Get the extension
    const extension = lowerFilename.split('.').pop();

    // Check if extension is in whitelist
    if (!ALLOWED_EXTENSIONS[extension]) {
        return {
            isValid: false,
            extension,
            error: `File type .${extension} is not allowed. Only .jpg, .jpeg, and .pdf files are accepted`
        };
    }

    return { isValid: true, extension, error: '' };
};

/**
 * Validate file content by checking magic numbers
 * @param {File} file - The file to validate
 * @returns {Promise<Object>} - { isValid: boolean, error: string }
 */
export const validateFileContent = async (file) => {
    try {
        // Read the first 4 bytes of the file
        const header = await readFileHeader(file, 4);

        // Get the file extension
        const extension = file.name.toLowerCase().split('.').pop();

        // Determine which signatures to check based on extension
        let signatures = [];
        if (extension === 'jpg' || extension === 'jpeg') {
            signatures = FILE_SIGNATURES.jpeg;
        } else if (extension === 'pdf') {
            signatures = FILE_SIGNATURES.pdf;
        } else {
            return {
                isValid: false,
                error: 'Unsupported file type'
            };
        }

        // Check if file content matches the expected signature
        const matches = matchesSignature(header, signatures);

        if (!matches) {
            return {
                isValid: false,
                error: `File content does not match .${extension} format. The file may be corrupted or disguised as a different type.`
            };
        }

        return { isValid: true, error: '' };
    } catch (error) {
        return {
            isValid: false,
            error: 'Failed to validate file content. Please try again.'
        };
    }
};

/**
 * Validate file size
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum allowed size in bytes
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateFileSize = (file, maxSize = MAX_FILE_SIZE) => {
    if (!file || !file.size) {
        return { isValid: false, error: 'Invalid file' };
    }

    if (file.size > maxSize) {
        const maxSizeMB = Math.round(maxSize / (1024 * 1024));
        return {
            isValid: false,
            error: `File size exceeds ${maxSizeMB}MB limit`
        };
    }

    return { isValid: true, error: '' };
};

/**
 * Sanitize filename to prevent path traversal and other attacks
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (filename) => {
    if (!filename) return '';

    // Remove path separators and null bytes
    let sanitized = filename.replace(/[\/\\:\0]/g, '');

    // Remove leading dots to prevent hidden files
    sanitized = sanitized.replace(/^\.+/, '');

    // Replace multiple dots with single dot (except the extension dot)
    const parts = sanitized.split('.');
    if (parts.length > 2) {
        const extension = parts.pop();
        const basename = parts.join('_');
        sanitized = `${basename}.${extension}`;
    }

    // Remove any special characters except alphanumeric, dash, underscore, and dot
    sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');

    // Limit filename length (keep extension)
    const maxLength = 255;
    if (sanitized.length > maxLength) {
        const ext = sanitized.split('.').pop();
        const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, maxLength - ext.length - 1);
        sanitized = `${truncatedName}.${ext}`;
    }

    return sanitized;
};

/**
 * Comprehensive file validation
 * Performs all security checks on an uploaded file
 * 
 * @param {File} file - The file to validate
 * @returns {Promise<Object>} - { isValid: boolean, errors: Array<string>, sanitizedName: string }
 */
export const validateUploadedFile = async (file) => {
    const errors = [];

    if (!file) {
        return {
            isValid: false,
            errors: ['No file provided'],
            sanitizedName: ''
        };
    }

    // 1. Validate file extension
    const extValidation = validateFileExtension(file.name);
    if (!extValidation.isValid) {
        errors.push(extValidation.error);
    }

    // 2. Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.isValid) {
        errors.push(sizeValidation.error);
    }

    // 3. Validate file content (magic numbers)
    // Only if extension and size are valid to avoid unnecessary processing
    if (extValidation.isValid && sizeValidation.isValid) {
        const contentValidation = await validateFileContent(file);
        if (!contentValidation.isValid) {
            errors.push(contentValidation.error);
        }
    }

    // 4. Sanitize filename
    const sanitizedName = sanitizeFilename(file.name);

    return {
        isValid: errors.length === 0,
        errors,
        sanitizedName
    };
};

/**
 * Format validation errors for display
 * @param {Array<string>} errors - Array of error messages
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (errors) => {
    if (!errors || errors.length === 0) return '';

    if (errors.length === 1) {
        return errors[0];
    }

    return errors.join('. ');
};
